import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { films } from "@/mocks/films";
import { filmCategoryMeta } from "@/mocks/categories";

interface SearchOverlayProps {
  open: boolean;
  onClose: () => void;
}

export default function SearchOverlay({ open, onClose }: SearchOverlayProps) {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setQuery("");
      const timer = window.setTimeout(() => inputRef.current?.focus(), 80);
      return () => window.clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const keyword = query.trim().toLowerCase();
  const results = useMemo(() => {
    if (!keyword) return [];
    return films.filter(
      (film) =>
        film.title.toLowerCase().includes(keyword) ||
        film.genres.some((genre) => genre.toLowerCase().includes(keyword)) ||
        film.cast.some((name) => name.toLowerCase().includes(keyword)) ||
        film.director.toLowerCase().includes(keyword)
    );
  }, [keyword]);

  const goDetail = (id: string) => {
    onClose();
    navigate(`/detail/film/${id}`);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center px-4 pt-24 md:pt-32">
      <div
        className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-2xl animate-fade-up rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
        <div className="flex items-center gap-3 rounded-md border border-background-300 bg-background-100 px-3 py-2.5">
          <span className="flex h-6 w-6 items-center justify-center text-foreground-500">
            <i className="ri-search-line text-lg" />
          </span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="搜索影视作品名称、题材或主创…"
            className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none md:text-base"
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭搜索"
            className="flex h-6 w-6 items-center justify-center text-foreground-500 hover:text-foreground-800"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="mt-4 max-h-[55vh] overflow-y-auto">
          {!keyword && (
            <div className="px-1 py-6 text-center">
              <p className="text-sm text-foreground-500">输入影视作品名称、题材或主创开始搜索</p>
              <div className="mt-3 flex flex-wrap justify-center gap-2">
                {["科幻", "悬疑", "家庭", "音乐"].map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => setQuery(tag)}
                    className="rounded-full bg-secondary-100 px-3 py-1.5 text-xs text-secondary-700 transition-colors hover:bg-secondary-200"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          )}

          {keyword && results.length === 0 && (
            <div className="px-1 py-10 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
                <i className="ri-emotion-unhappy-line text-2xl" />
              </div>
              <p className="text-sm text-foreground-600">
                没有找到与“{query}”相关的影视作品
              </p>
              <p className="mt-1 text-xs text-foreground-400">试试更换关键词，如题材或导演姓名</p>
            </div>
          )}

          {keyword && results.length > 0 && (
            <ul className="flex flex-col gap-1.5">
              {results.map((film) => (
                <li key={film.id}>
                  <button
                    type="button"
                    onClick={() => goDetail(film.id)}
                    className="flex w-full items-center gap-3 rounded-md p-2 text-left transition-colors hover:bg-background-100"
                  >
                    <div className="h-16 w-12 shrink-0 overflow-hidden rounded-md bg-secondary-100">
                      <img
                        src={film.cover}
                        alt={film.title}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-foreground-900">
                        {film.title}
                      </p>
                      <p className="mt-0.5 truncate text-xs text-foreground-500">
                        {filmCategoryMeta[film.category].label} · {film.genres.join(" · ")} ·{" "}
                        {film.year}
                      </p>
                    </div>
                    <span className="flex items-center gap-1 text-sm font-semibold text-accent-600">
                      <i className="ri-star-fill text-xs" />
                      {film.rating.toFixed(1)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}